import { Box, Flex, Tag, Text } from "@chakra-ui/react"
import { useRouter } from "next/router"
import { Suggestion } from "../../../../api-clients/suggest-openapi"
import { fhAnalyticsUserActions, pages } from "../../../../common/constants/constants"
import { useBrandedTranslation } from "../../../../common/hooks/useBrandedTranslation"
import sendFhAnalytics from "../../../../common/lib/fredhopper"
import { redirectToPage } from "../../../../common/utils/router-utils"
import CustomImage from "../../../shared/CustomImage"
import PhasingOutIndicator from "../../../shared/PhasingOutIndicator"
import SuggestionRow from "../SuggestionRow"

type Props = {
  suggestions: Array<Suggestion>
  handleResetSearchField: () => void
}

const ProductSuggestions = ({ suggestions, handleResetSearchField }: Props) => {
  const router = useRouter()
  const { t } = useBrandedTranslation()

  /* Guard clause if no product suggestions */
  if (!Array.isArray(suggestions) || suggestions.length === 0) {
    return null
  }

  const handleClick = (productId: string) => {
    //wait until analytics is sent as otherwise analytics call is cancelled
    sendFhAnalytics({
      locale: router.locale,
      action: fhAnalyticsUserActions.SUGG_PROCLICK,
      itemId: productId,
      shouldBeInterrupted: true,
    }).finally(() => {
      handleResetSearchField()
      redirectToPage(router, pages.PRODUCT, productId)
    })
  }

  return (
    <>
      {suggestions.map((sugg) => {
        const isNew = sugg.novelty_product === "1"
        const isPhasedOut = sugg.phase_out_indicator === "1"
        const hasSeries = !!sugg.series
        const hasTag = isNew || hasSeries

        //Workaround for images until backend implements _thumbnail attribute
        const thumbnailSrc = sugg._imageurl.replace("Medium", "Thumbnail").replace("Small", "Thumbnail")

        return (
          <SuggestionRow key={sugg.productid} onMouseDown={() => handleClick(sugg.productid)}>
            <CustomImage src={thumbnailSrc} alt="product suggestion image" minW="10" h="10" mr="4" />
            <Flex w="100%" align="center" justify="space-between">
              <Flex w="100%" flexDirection="column">
                <Flex w="100%" justify="space-between">
                  <Text mb="1.5" className="suggestion-name">
                    {sugg.name}
                  </Text>
                  {hasTag && (
                    <Flex gap="2" ml="2" alignSelf="flex-start" justifySelf="flex-end" flexShrink="0">
                      {isNew && (
                        <Tag variant="indicatorNew" size="xs">
                          {t("indicator-new")}
                        </Tag>
                      )}
                      {hasSeries && (
                        <Tag variant="series" size="xs">
                          {sugg.series}
                        </Tag>
                      )}
                    </Flex>
                  )}
                </Flex>
                <Text fontSize="2xs" color="gray.700">
                  {sugg.amount_articles} {t("pdp-tile-article-unit", { count: +sugg.amount_articles })}
                </Text>
                {isPhasedOut && (
                  <Box mt="2">
                    <PhasingOutIndicator />
                  </Box>
                )}
              </Flex>
            </Flex>
          </SuggestionRow>
        )
      })}
    </>
  )
}

export default ProductSuggestions
